import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';

const SettingsContainer = styled.div`
  padding: 0 5% 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const PageTabs = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 30px;
    flex-wrap: wrap;
`;

const PageTab = styled.button`
    padding: 10px 20px;
    background: ${props => props.$active ? 'var(--gradient-pink)' : 'var(--glass-bg)'};
    border: 1px solid ${props => props.$active ? 'var(--gradient-pink)' : 'var(--border-color)'};
    border-radius: 8px;
    color: ${props => props.$active ? 'white' : 'var(--primary-color)'};
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:hover {
        background: ${props => props.$active ? 'var(--gradient-pink)' : 'rgba(255, 255, 255, 0.1)'};
    }
`;

const EditorContainer = styled.div`
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 25px;
    margin-bottom: 20px;
`;

const SettingItem = styled.div`
    margin-bottom: 20px;
    
    label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: var(--primary-color);
    }
    
    input, textarea {
        width: 100%;
        padding: 12px;
        background: rgba(0, 0, 0, 0.3);
        border: 1px solid var(--border-color);
        border-radius: 8px;
        color: var(--primary-color);
        font-size: 1rem;
        
        &:focus {
            outline: none;
            border-color: var(--gradient-pink);
        }
    }
    
    textarea {
        min-height: 400px;
        resize: vertical;
        font-family: 'Consolas', 'Monaco', monospace;
        line-height: 1.6;
    }
`;

const SaveButton = styled.button`
    padding: 12px 30px;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: var(--gradient-pink);
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-right: 10px;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(255, 0, 127, 0.3);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const PreviewButton = styled.button`
    padding: 12px 30px;
    font-size: 1rem;
    font-weight: 600;
    border: 1px solid var(--border-color);
    border-radius: 10px;
    background: transparent;
    color: var(--primary-color);
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const SuccessMessage = styled.div`
    background: rgba(46, 213, 115, 0.2);
    border: 1px solid #2ed573;
    color: #2ed573;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
`;

const ErrorMessage = styled.div`
    background: rgba(255, 71, 87, 0.2);
    border: 1px solid #ff4757;
    color: #ff4757;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
`;

const HelpText = styled.div`
    background: rgba(52, 152, 219, 0.1);
    border: 1px solid #3498db;
    color: var(--secondary-color);
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-size: 0.9rem;
    
    h4 {
        color: #3498db;
        margin-bottom: 10px;
        font-size: 1rem;
    }
    
    code {
        background: rgba(0, 0, 0, 0.3);
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'Consolas', 'Monaco', monospace;
    }
`;

const AdminPagesPage = () => {
    const pages = [
        { id: 'privacy', name: 'Gizlilik Politikası', url: '/privacy' },
        { id: 'cookie-policy', name: 'Çerez Politikası', url: '/cookie-policy' },
        { id: 'about', name: 'Biz Kimiz?', url: '/about' },
        { id: 'contact', name: 'İletişim', url: '/contact' },
        { id: 'support', name: 'Destek', url: '/support' }
    ];
    
    const [activePage, setActivePage] = useState('privacy');
    const [pageData, setPageData] = useState({ title: '', content: '' });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    useEffect(() => {
        loadPageData();
    }, [activePage]);

    const loadPageData = async () => {
        try {
            const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_PAGES)}?page=${activePage}`);
            const data = await response.json();
            
            if (data.success) {
                setPageData(data.page);
            }
        } catch (error) {
            console.error('Sayfa yüklenirken hata:', error);
        }
    };

    const handleSave = async () => {
        if (!pageData.title.trim() || !pageData.content.trim()) {
            setMessage('Başlık ve içerik alanları boş bırakılamaz!');
            setMessageType('error');
            return;
        }

        setLoading(true);
        setMessage('');
        
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.SAVE_PAGES), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    page: activePage,
                    title: pageData.title,
                    content: pageData.content
                })
            });

            const data = await response.json();
            
            if (data.success) {
                setMessage('Sayfa başarıyla kaydedildi!');
                setMessageType('success');
            } else {
                throw new Error(data.message || 'Kayıt başarısız');
            }
        } catch (error) {
            console.error('Sayfa kaydedilirken hata:', error);
            setMessage('Sayfa kaydedilirken hata oluştu!');
            setMessageType('error');
        } finally {
            setLoading(false);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handlePreview = () => {
        const currentPageData = pages.find(p => p.id === activePage);
        if (currentPageData) {
            window.open(currentPageData.url, '_blank');
        }
    };

    return (
        <SettingsContainer>
            <Header>
                <Title>Sayfa Editörü</Title>
            </Header>

            {message && (
                messageType === 'success' ? (
                    <SuccessMessage>✅ {message}</SuccessMessage>
                ) : (
                    <ErrorMessage>❌ {message}</ErrorMessage>
                )
            )}

            <PageTabs>
                {pages.map(page => (
                    <PageTab
                        key={page.id}
                        $active={activePage === page.id}
                        onClick={() => setActivePage(page.id)}
                    >
                        {page.name}
                    </PageTab>
                ))}
            </PageTabs>

            <HelpText>
                <h4>HTML Editörü Kullanım Kılavuzu:</h4>
                <p>• <code>&lt;h2&gt;&lt;/h2&gt;</code>, <code>&lt;h3&gt;&lt;/h3&gt;</code>, <code>&lt;h4&gt;&lt;/h4&gt;</code> - Başlık etiketleri</p>
                <p>• <code>&lt;p&gt;&lt;/p&gt;</code> - Paragraf, <code>&lt;strong&gt;&lt;/strong&gt;</code> - Kalın yazı, <code>&lt;em&gt;&lt;/em&gt;</code> - İtalik</p>
                <p>• <code><ul><li>...</li></ul></code> - Madde işaretli liste</p>
                <p>• <code><a href="url">Link</a></code> - Link, <code><br /></code> - Satır atlatma</p>
            </HelpText>

            <EditorContainer>
                <SettingItem>
                    <label>Sayfa Başlığı</label>
                    <input
                        type="text"
                        value={pageData.title || ''}
                        onChange={(e) => setPageData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Sayfa başlığını girin..."
                    />
                </SettingItem>
                
                <SettingItem>
                    <label>Sayfa İçeriği (HTML)</label>
                    <textarea
                        value={pageData.content || ''}
                        onChange={(e) => setPageData(prev => ({ ...prev, content: e.target.value }))}
                        placeholder="HTML içeriğini buraya girin..."
                    />
                </SettingItem>
            </EditorContainer>

            <div>
                <SaveButton onClick={handleSave} disabled={loading}>
                    {loading ? 'Kaydediliyor...' : 'Sayfayı Kaydet'}
                </SaveButton>
                <PreviewButton onClick={handlePreview}>
                    Sayfayı Önizle
                </PreviewButton>
            </div>
        </SettingsContainer>
    );
};

export default AdminPagesPage;
